import { useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import {
  CONTENT_TYPES,
  EXAM_KINDS,
  type ContentType,
  type ExamKind,
  EXAM_TYPES,
  type ExamType,
} from '../../domain/entities/content.entity';
import { useCreateContentItem } from '../hooks/use-content-items';
import { InstitutionSelect } from './InstitutionSelect';
import {
  CONTENT_TYPE_LABELS,
  EXAM_KIND_LABELS,
  EXAM_TYPE_LABELS,
  getApiErrorMessage,
} from '../display';
import { Textarea } from '@/shared/components/textarea';

interface CreateContentItemModalProps {
  open: boolean;
  onClose: () => void;
}

interface Alternative {
  id: string;
  text: string;
}

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const newAlternatives = (): Alternative[] => [
  { id: 'a', text: '' },
  { id: 'b', text: '' },
];

export function CreateContentItemModal({
  open,
  onClose,
}: CreateContentItemModalProps) {
  const [type, setType] = useState<ContentType>('QUESTION');

  // QUESTION
  const [qFormat, setQFormat] = useState<'MULTIPLE_CHOICE' | 'ESSAY'>(
    'MULTIPLE_CHOICE',
  );
  const [stem, setStem] = useState('');
  const [alternatives, setAlternatives] =
    useState<Alternative[]>(newAlternatives());
  const [correctId, setCorrectId] = useState('a');
  const [expectedAnswer, setExpectedAnswer] = useState('');

  // VIDEO
  const [url, setUrl] = useState('');
  const [durationSec, setDurationSec] = useState('');
  const [transcript, setTranscript] = useState('');

  // EXAM
  const [year, setYear] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [variant, setVariant] = useState('');
  const [examKind, setExamKind] = useState<ExamKind>('REAL_EXAM');
  const [examType, setExamType] = useState<ExamType>('DIRECT_ACCESS');

  // FLASHCARD
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');

  // OFFICIAL_COMMENT
  const [text, setText] = useState('');

  const create = useCreateContentItem();

  const reset = () => {
    setType('QUESTION');
    setQFormat('MULTIPLE_CHOICE');
    setStem('');
    setAlternatives(newAlternatives());
    setCorrectId('a');
    setExpectedAnswer('');
    setUrl('');
    setDurationSec('');
    setTranscript('');
    setYear('');
    setInstitutionId('');
    setVariant('');
    setExamKind('REAL_EXAM');
    setExamType('DIRECT_ACCESS');
    setFront('');
    setBack('');
    setText('');
  };

  const close = () => {
    create.reset();
    reset();
    onClose();
  };

  const addAlternative = () => {
    const id = LETTERS[alternatives.length] ?? `alt${alternatives.length}`;
    setAlternatives((prev) => [...prev, { id, text: '' }]);
  };

  const removeAlternative = (id: string) => {
    setAlternatives((prev) => prev.filter((a) => a.id !== id));
    if (correctId === id) setCorrectId('a');
  };

  const setAlternativeText = (id: string, value: string) => {
    setAlternatives((prev) =>
      prev.map((a) => (a.id === id ? { ...a, text: value } : a)),
    );
  };

  const buildBody = (): Record<string, unknown> => {
    switch (type) {
      case 'QUESTION':
        if (qFormat === 'ESSAY') {
          // Opcional: vazia, o campo nem vai — o back não grava resposta
          // esperada sem conteúdo.
          return {
            format: 'ESSAY',
            stem: stem.trim(),
            ...(expectedAnswer.trim()
              ? { expectedAnswer: expectedAnswer.trim() }
              : {}),
          };
        }
        return {
          format: 'MULTIPLE_CHOICE',
          stem: stem.trim(),
          alternatives: alternatives.map((a) => ({
            id: a.id,
            text: a.text.trim(),
          })),
          correctAlternativeId: correctId,
        };
      case 'VIDEO':
        return {
          url: url.trim(),
          ...(durationSec ? { durationSec: Number(durationSec) } : {}),
          ...(transcript.trim() ? { transcript: transcript.trim() } : {}),
        };
      case 'EXAM':
        return {
          ...(year ? { year: Number(year) } : {}),
          ...(institutionId.trim()
            ? { institutionId: institutionId.trim() }
            : {}),
          ...(variant.trim() ? { variant: variant.trim() } : {}),
          examKind,
          examType,
        };
      case 'FLASHCARD':
        return { front: front.trim(), back: back.trim() };
      case 'OFFICIAL_COMMENT':
        return { text: text.trim() };
    }
  };

  const isBodyValid = (): boolean => {
    switch (type) {
      case 'QUESTION':
        if (!stem.trim()) return false;
        if (qFormat === 'ESSAY') return true;
        return (
          alternatives.length >= 2 &&
          alternatives.every((a) => a.text.trim() !== '') &&
          alternatives.some((a) => a.id === correctId)
        );
      case 'VIDEO':
        return url.trim() !== '';
      case 'EXAM':
        // O `ExamHandler` exige os quatro; validar aqui evita um 400 previsível.
        return (
          year !== '' && institutionId.trim() !== '' && variant.trim() !== ''
        );
      case 'FLASHCARD':
        return front.trim() !== '' && back.trim() !== '';
      case 'OFFICIAL_COMMENT':
        return text.trim() !== '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBodyValid()) return;
    create.mutate({ type, body: buildBody() }, { onSuccess: close });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Criar conteúdo"
      description="O item nasce como rascunho (DRAFT). Publique-o depois na tela de detalhe."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Tipo"
          value={type}
          onChange={(e) => setType(e.target.value as ContentType)}
          options={CONTENT_TYPES.map((t) => ({
            value: t,
            label: CONTENT_TYPE_LABELS[t],
          }))}
        />

        {type === 'QUESTION' && (
          <div className="space-y-4">
            <Select
              label="Formato"
              value={qFormat}
              onChange={(e) =>
                setQFormat(e.target.value as 'MULTIPLE_CHOICE' | 'ESSAY')
              }
              options={[
                { value: 'MULTIPLE_CHOICE', label: 'Múltipla escolha' },
                { value: 'ESSAY', label: 'Dissertativa' },
              ]}
            />
            <Textarea
              label="Enunciado"
              value={stem}
              onChange={setStem}
              placeholder="Enunciado da questão…"
              rows={3}
            />
            {qFormat === 'ESSAY' && (
              <Textarea
                label="Resposta esperada (opcional)"
                value={expectedAnswer}
                onChange={setExpectedAnswer}
                placeholder="O que o corretor espera ler na resposta…"
                rows={3}
              />
            )}
            {qFormat === 'MULTIPLE_CHOICE' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-(--color-text-primary) text-sm font-medium">
                    Alternativas
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addAlternative}
                  >
                    Adicionar alternativa
                  </Button>
                </div>
                {alternatives.map((alt) => (
                  <div key={alt.id} className="flex items-center gap-2">
                    <span className="text-(--color-text-secondary) w-6 shrink-0 font-mono text-sm uppercase">
                      {alt.id}
                    </span>
                    <div className="flex-1">
                      <Input
                        value={alt.text}
                        onChange={(e) =>
                          setAlternativeText(alt.id, e.target.value)
                        }
                        placeholder={`Alternativa ${alt.id.toUpperCase()}`}
                      />
                    </div>
                    {alternatives.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeAlternative(alt.id)}
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                ))}
                <Select
                  label="Alternativa correta"
                  value={correctId}
                  onChange={(e) => setCorrectId(e.target.value)}
                  options={alternatives.map((a) => ({
                    value: a.id,
                    label: `${a.id.toUpperCase()} — ${a.text.trim() || '(vazia)'}`,
                  }))}
                />
              </div>
            )}
          </div>
        )}

        {type === 'VIDEO' && (
          <div className="space-y-4">
            <Input
              label="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              required
            />
            <Input
              label="Duração (segundos)"
              type="number"
              min={0}
              value={durationSec}
              onChange={(e) => setDurationSec(e.target.value)}
            />
            <Textarea
              label="Transcrição (opcional)"
              value={transcript}
              onChange={setTranscript}
              rows={3}
            />
          </div>
        )}

        {type === 'EXAM' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Ano"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              required
            />
            {/* UF + instituição, no lugar do UUID digitado à mão. */}
            <InstitutionSelect
              value={institutionId}
              onChange={setInstitutionId}
              required
            />
            <Input
              label="Variante"
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              required
            />
            {/* Dois eixos distintos, e os rótulos dizem qual é qual: "tipo"
                é a trilha da residência; real × simulado é outra pergunta. */}
            <Select
              label="Tipo da prova"
              value={examType}
              onChange={(e) => setExamType(e.target.value as ExamType)}
              options={EXAM_TYPES.map((examTypeOption) => ({
                value: examTypeOption,
                label: EXAM_TYPE_LABELS[examTypeOption],
              }))}
            />
            <Select
              label="Prova real ou simulado"
              value={examKind}
              onChange={(e) => setExamKind(e.target.value as ExamKind)}
              options={EXAM_KINDS.map((kind) => ({
                value: kind,
                label: EXAM_KIND_LABELS[kind],
              }))}
            />
          </div>
        )}

        {type === 'FLASHCARD' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Textarea label="Frente" value={front} onChange={setFront} />
            <Textarea label="Verso" value={back} onChange={setBack} />
          </div>
        )}

        {type === 'OFFICIAL_COMMENT' && (
          <Textarea
            label="Texto"
            value={text}
            onChange={setText}
            rows={4}
            placeholder="Comentário oficial…"
          />
        )}

        {create.error && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(create.error, 'Falha ao criar conteúdo')}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!isBodyValid() || create.isPending}>
            {create.isPending ? 'Criando…' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
